<template>
  <div ref="element" class="card stroke-m neutral-50 outlined">
    <div class="controls">
      <button
        type="button"
        class="button-close"
        aria-label="close"
        @click="close"
      >
        <icon name="material-symbols-light:close-small-rounded" size="2.4rem" />
      </button>
    </div>
    <div class="scrollable">
      <div class="content">
        <div class="header">
          <div class="img-wrapper">
            <img
              v-if="type === 'pairing'"
              src="~/assets/images/popin-content-pairing.webp"
              srcset="~/assets/images/popin-content-pairing@2x.webp 2x"
              alt="Contenu Nomad+"
              width="362"
              height="278"
            />
            <img
              v-else
              src="~/assets/images/popin-content-default.webp"
              srcset="~/assets/images/popin-content-default@2x.webp 2x"
              alt="Contenu Nomad+"
              width="362"
              height="193"
            />
          </div>

          <p v-if="type === 'pairing'" class="description">
            Débloque <strong>TOUTES</strong> les paires parfaites +
            <strong>TOUS</strong> les contenus de l’app en illimité avec
            <strong>NOMAD+</strong> ! ❤️‍🔥
          </p>
          <p v-else class="description">
            C’est le moment d’avoir <strong>TOUS</strong> les contenus en
            illimité avec <strong>NOMAD+</strong> ❤️‍🔥
          </p>
        </div>
        <div v-if="profile.isParent" class="actions">
          <ButtonBase
            rounded
            theme="secondary"
            variant="tonal"
            stroke="n"
            :to="`${runtimeConfig.public.eva1Url}/premium`"
          >
            Activer
          </ButtonBase>
        </div>
        <div v-else class="actions">
          <ButtonBase
            rounded
            theme="secondary"
            variant="tonal"
            stroke="n"
            :to="`${runtimeConfig.public.eva1Url}/premium`"
          >
            Let's gooooooo!
          </ButtonBase>
          <ButtonBase
            :theme="'secondary-light'"
            stroke="n"
            class="button-share"
            rounded
            alt="partager"
            @click="handleShare"
          >
            <template v-if="profile.isParent">
              <icon class="icon" name="shareMail" /> Partager
            </template>
            <template v-else>
              <icon class="icon" name="shareMail" /> Je demande à mes parents
            </template>
          </ButtonBase>
        </div>

        <aside v-if="!profile.isParent">
          <h3 class="aside-title">Besoin d’aide pour les convaincre ?</h3>
          <ButtonBlockIconArrow align="left" @click="emit('openSendMail')">
            <template #icon><icon name="nomad:letter" size="6rem" /></template>
            Partage nous le contact de tes parents et on s’occupe de tout !
          </ButtonBlockIconArrow>
        </aside>
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
interface Props {
  type?: string;
  appEventShare: UseAppEventShare;
  backUrl?: string;
}

const props = defineProps({
  type: { default: 'default' },
  appEventShare: null,
  backUrl: null
});

const emit = defineEmits(["openSendMail", "close"]);

const element = ref(null);

const { close } = useDialogControls(element, { closeClickOutside: true }, emit);

const runtimeConfig = useRuntimeConfig();

const profile = useProfileStore();

const { t } = useNuxtApp().$i18n;

const i18nId = computed(() => {
  switch (props.type) {
    case 'pairing':
    case 'flashcard':
    case 'essential':
    case 'annale':
    case 'practice':
      return `share.locked_${props.type}`;
    default:
      return `share.locked_default`;
  }
});

const { handleShare } = useShareNomad(t(i18nId.value), () => {
  if (props.backUrl) navigateTo(props.backUrl);
  emit('close');
});
</script>
<style lang="scss" scoped>
.card {
  display: flex;
  position: relative;
  flex-direction: column;
  align-items: center;
  min-width: 30rem;
  max-width: 50rem;
  max-height: calc(
    100vh - var(--header_height) - var(--row_gap) - var(--row_gap)
  );
  max-height: calc(
    100dvh - var(--header_height) - var(--row_gap) - var(--row_gap)
  );
  padding: var(--gap_l);
  box-shadow: rgb(0 0 0 / 16%) 0 10px 36px 0, rgb(0 0 0 / 6%) 0 0 0 1px;
  justify-self: center;
  gap: var(--gap_l);

  --gap_elements: 6rem;

  .scrollable {
    width: calc(100% + 1.8rem);
    min-height: 10rem;
    margin-left: 1.8rem;
    padding-right: 1.8rem;
    overflow-y: auto;
    transition: opacity 300ms $ease-out-quad;

    &::-webkit-scrollbar {
      width: 0.5rem;
    }
    &::-webkit-scrollbar-thumb,
    &::-webkit-scrollbar-track {
      border-radius: 0.25rem;
    }

    &::-webkit-scrollbar-thumb {
      background: var(--color_neutral_100);
    }

    &::-webkit-scrollbar-track {
      background: var(--color_neutral_60);
    }

    .content {
      display: flex;
      flex-direction: column;
      width: 100%;
      gap: var(--gap_l);
    }
  }
  &.show {
    .scrollable {
      opacity: 1;
    }
  }
}

.actions {
  display: flex;
  flex-direction: column;
  gap: var(--gap_s);
}

.header {
  display: flex;
  flex-direction: column;
  gap: var(--gap_l);
}

.description {
  @include font('m', 400, 'secondary');

  color: var(--color_neutral_900);
  text-align: center;
  strong {
    @include font('n', 900);
  }
}

aside {
  display: flex;
  flex-direction: column;
  gap: var(--gap_s);
}

.aside-title {
  @include font('m', 800, 'secondary');

  color: var(--color_secondary);
  text-align: center;
}

.img-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--gap_m) var(--gap_m) 0;
  border-width: var(--stroke_thickness_n);
  border-style: solid;
  border-radius: 1.2rem;
  border-color: var(--color_neutral_100);
  background-color: var(--color_neutral_80);
  :deep(img) {
    width: auto;
    height: 100%;
    height: 14rem;
    @include above('m') {
      height: 20rem;
    }
  }
}

.controls {
  display: flex;
  position: absolute;
  z-index: 2;
  top: 0.5rem;
  right: 0.5rem;
  flex-direction: row-reverse;
  width: 100%;
}

.button-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.8rem;
  height: 1.8rem;
  overflow: hidden;
  transform: translate(0, 0);
  transition: transform 500ms $ease-out-quad, box-shadow 200ms $ease-in-quad;
  border-radius: 50%;
  background-color: var(--color_error);
  color: var(--color_neutral_25);
  &:hover,
  &:focus {
    transform: rotate(180deg);
  }
}
</style>
